import React from "react";
import { Badge, Button, Card, CardBody, Table } from "reactstrap";
import type { RootState } from "@/services/store";

/**
 * One held submission: `FormSubmissionListItem` (shared/apiTypes.ts), read off
 * the slice that stores it so the view needs no six-level relative import.
 */
export type FormSubmissionListItem = RootState["heldSubmissions"]["items"][number];

/** The three hold rules of SCRUM-1751, as a reviewer reads them. */
const REASON_LABELS: Record<NonNullable<FormSubmissionListItem["heldReason"]>, string> = {
  duplicate_message: "Duplicate message",
  ip_burst: "Sending burst",
  link_count: "Too many links",
};

const MESSAGE_PREVIEW = 120;

const formatReceived = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

export type HeldSubmissionsViewProps = {
  items: FormSubmissionListItem[];
  loading: boolean;
  /** Row whose full message is shown. */
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  /** Opens the confirm dialog. */
  onRelease: (row: FormSubmissionListItem) => void;
  /** Opens the confirm dialog. */
  onDiscard: (row: FormSubmissionListItem) => void;
};

/**
 * The held-submissions table (SCRUM-1753): one row per held public form
 * submission, newest first as the server lists them, Release and Discard per
 * row. Selectors for tests and e2e are `data-testid`, never a class
 * (SCRUM-1649).
 */
const HeldSubmissionsView = ({
  items,
  loading,
  expandedId,
  onToggleExpand,
  onRelease,
  onDiscard,
}: HeldSubmissionsViewProps) => (
  <Card className="shadow-sm">
    <CardBody>
      {items.length === 0 ? (
        <p className="mb-0 text-muted" data-testid="held-empty">
          {loading ? "Loading…" : "No held submissions"}
        </p>
      ) : (
        <Table size="sm" responsive className="mb-0 align-items-center">
          <thead className="thead-light">
            <tr>
              <th>Received</th>
              <th>Site</th>
              <th>Sender</th>
              <th>Reason</th>
              <th>Message</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const message = row.message || "";
              const expanded = expandedId === row._id;
              const preview =
                !expanded && message.length > MESSAGE_PREVIEW
                  ? `${message.slice(0, MESSAGE_PREVIEW)}…`
                  : message;
              return (
                <tr key={row._id} data-testid={`held-row-${row._id}`}>
                  <td>{formatReceived(row.receivedAt)}</td>
                  <td>{row.site?.domain || "—"}</td>
                  <td>
                    {row.senderName}
                    <br />
                    <small>{row.senderAddress}</small>
                  </td>
                  <td>
                    <Badge color="warning" pill>
                      {(row.heldReason && REASON_LABELS[row.heldReason]) || row.heldReason}
                    </Badge>
                  </td>
                  <td style={{ whiteSpace: "pre-wrap", maxWidth: "28rem" }}>
                    {preview}
                    {message.length > MESSAGE_PREVIEW && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 ml-1"
                        onClick={() => onToggleExpand(row._id)}
                      >
                        {expanded ? "less" : "more"}
                      </Button>
                    )}
                  </td>
                  <td className="text-right text-nowrap">
                    <Button
                      size="sm"
                      color="success"
                      data-testid={`held-release-${row._id}`}
                      onClick={() => onRelease(row)}
                    >
                      Release
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      data-testid={`held-discard-${row._id}`}
                      onClick={() => onDiscard(row)}
                    >
                      Discard
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </CardBody>
  </Card>
);

export default HeldSubmissionsView;
